import { CheckboxTd, CheckboxTh, ScrollArea, Section, SectionHeader } from '../styles/components/layout.styles';
import React, { useMemo } from 'react';
import SkapaTable, { TableBody as SkapaTableBody, TableHeader as SkapaTableHeader } from '@ingka/table';

import Button from '@ingka/button';
import Checkbox from '@ingka/checkbox';
import { SectionLabel } from '../styles/components/primitives.styles';

export interface VerificationColumn<T> {
    header: string;
    render: (item: T) => React.ReactNode;
}

interface VerificationTableProps<T> {
    label: string;
    items: T[];
    columns: VerificationColumn<T>[];
    getItemId: (item: T) => string;
    selectedIds: string[];
    onChangeSelectedIds: (ids: string[]) => void;
    isSubmitting: boolean;
    getRowStyle?: (item: T) => React.CSSProperties;
}

export const VerificationTable = <T,>({
    label,
    items,
    columns,
    getItemId,
    selectedIds,
    onChangeSelectedIds,
    isSubmitting,
    getRowStyle,
}: VerificationTableProps<T>) => {
    const allIds = useMemo(() => items.map(getItemId), [items, getItemId]);
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const allSelected = allIds.length > 0 && allIds.every((id) => selectedSet.has(id));

    // Build DOM-safe ids; data ids may be invalid or duplicated.
    const domIdPrefix = useMemo(() => label.toLowerCase().replace(/\W+/g, '-'), [label]);

    const toggleAll = () => {
        onChangeSelectedIds(allSelected ? [] : allIds);
    };

    const toggle = (id: string) => {
        onChangeSelectedIds(
            selectedSet.has(id)
                ? selectedIds.filter((x) => x !== id)
                : [...selectedIds, id]
        );
    };

    return (
        <Section>
            <SectionHeader>
                <SectionLabel>
                    {label} ({selectedIds.length}/{items.length} selected)
                </SectionLabel>
                <Button type='tertiary' small onClick={toggleAll} disabled={isSubmitting}>
                    {allSelected ? 'Deselect all' : 'Select all'}
                </Button>
            </SectionHeader>
            <ScrollArea>
                <SkapaTable fullWidth>
                    <SkapaTableHeader>
                        <tr>
                            <CheckboxTh>Include</CheckboxTh>
                            {columns.map((col) => (
                                <th key={col.header}>{col.header}</th>
                            ))}
                        </tr>
                    </SkapaTableHeader>
                    <SkapaTableBody>
                        {items.map((item, index) => {
                            const id = getItemId(item);
                            const rowStyle = getRowStyle?.(item);
                            return (
                                <tr key={id}>
                                    <CheckboxTd>
                                        <Checkbox
                                            id={`${domIdPrefix}-row-${index}`}
                                            value={id}
                                            checked={selectedSet.has(id)}
                                            disabled={isSubmitting}
                                            onChange={() => toggle(id)}
                                        />
                                    </CheckboxTd>
                                    {columns.map((col) => (
                                        <td key={col.header} style={rowStyle}>{col.render(item)}</td>
                                    ))}
                                </tr>
                            );
                        })}
                    </SkapaTableBody>
                </SkapaTable>
            </ScrollArea>
        </Section>
    );
};